import {
  type RunWorkerActionType,
  SystemActionType,
  UserTaskType,
  WorkflowConfig,
  WorkflowStepType,
  WorkflowTaskTypes,
  WorkflowType,
} from "@validereinc/domain";
import { Workflows } from "@validereinc/domain-controllers";

const { flattenWorkflowSteps } = Workflows.logic;

export const getFormSchemaIdFromWorkflowConfiguration = (
  stepId?: string,
  workflowConfiguration?: WorkflowType["config"]
) => {
  if (!workflowConfiguration || !stepId) {
    return;
  }

  const allWorkflowSteps = flattenWorkflowSteps(workflowConfiguration.steps);
  const associatedStepConfig = allWorkflowSteps[stepId];

  if (
    !associatedStepConfig ||
    associatedStepConfig.type !== WorkflowStepType.USER_TASK ||
    associatedStepConfig.task?.type !== UserTaskType.SUBMIT_FORM
  ) {
    return;
  }

  return associatedStepConfig.task.form_schema_id;
};

export const getDatasetDetailsFromWorkflowConfiguration = (
  stepId?: string,
  workflowConfiguration?: WorkflowType["config"]
) => {
  if (!workflowConfiguration || !stepId) {
    return {
      datasetId: "",
      datasetRequired: false,
    };
  }

  const allWorkflowSteps = flattenWorkflowSteps(workflowConfiguration.steps);
  const associatedStepConfig = allWorkflowSteps[stepId];

  if (
    !associatedStepConfig ||
    associatedStepConfig.type !== WorkflowStepType.USER_TASK ||
    associatedStepConfig.task?.type !== UserTaskType.SUBMIT_DATASET
  ) {
    return {
      datasetId: "",
      datasetRequired: false,
    };
  }

  return {
    datasetId: associatedStepConfig.task.dataset_id,
    datasetRequired: Boolean(associatedStepConfig.task.dataset_required),
  };
};

export const getEventSchemaIdFromWorkflowUserTaskConfiguration = (
  stepId?: string,
  workflowConfiguration?: WorkflowType["config"]
) => {
  if (!workflowConfiguration || !stepId) {
    return;
  }

  const allWorkflowSteps = flattenWorkflowSteps(workflowConfiguration.steps);
  const associatedStepConfig = allWorkflowSteps[stepId];

  if (
    !associatedStepConfig ||
    associatedStepConfig.type !== WorkflowStepType.USER_TASK ||
    associatedStepConfig?.task?.type !== UserTaskType.COMPLETE_EVENT
  ) {
    return;
  }

  const {
    task: { event_step_id: eventStepId },
  } = associatedStepConfig;

  const eventAssociatedStepConfig = allWorkflowSteps[eventStepId];

  if (
    !eventAssociatedStepConfig ||
    eventAssociatedStepConfig?.task?.type !== "create_event"
  ) {
    return;
  }

  return eventAssociatedStepConfig.task.event_schema_id;
};

export const getEventSchemaIdFromWorkflowSystemActionConfiguration = (
  stepId?: string,
  workflowConfiguration?: WorkflowType["config"]
) => {
  if (!workflowConfiguration || !stepId) {
    return;
  }

  const allWorkflowSteps = flattenWorkflowSteps(workflowConfiguration.steps);
  const associatedStepConfig = allWorkflowSteps[stepId];

  if (
    !associatedStepConfig ||
    associatedStepConfig.type !== WorkflowStepType.SYSTEM_ACTION ||
    associatedStepConfig.task?.type !== SystemActionType.CREATE_EVENT
  ) {
    return;
  }

  return associatedStepConfig.task.event_schema_id;
};

export const getChoicesFromWorkflowConfiguration = (
  stepId?: string,
  workflowConfiguration?: WorkflowConfig
) => {
  const allWorkflowSteps = flattenWorkflowSteps(workflowConfiguration?.steps);
  if (
    stepId &&
    allWorkflowSteps[stepId]?.task?.type === WorkflowTaskTypes.CHOICE
  ) {
    return allWorkflowSteps[stepId]?.task.choices;
  }
  return [];
};

export const getRecordFilterFromWorkflowConfiguration = (
  stepId?: string,
  workflowConfiguration?: WorkflowType["config"]
) => {
  const allWorkflowSteps = flattenWorkflowSteps(workflowConfiguration?.steps);

  if (
    !stepId ||
    !(
      allWorkflowSteps[stepId] &&
      "record_filter" in allWorkflowSteps[stepId].task
    )
  ) {
    return;
  }

  return allWorkflowSteps[stepId]?.task.record_filter;
};

export const getReportIdFromWorkflowConfiguration = (
  stepId?: string,
  workflowConfiguration?: WorkflowType["config"]
) => {
  const allWorkflowSteps = flattenWorkflowSteps(workflowConfiguration?.steps);

  if (
    !workflowConfiguration ||
    !stepId ||
    !(allWorkflowSteps[stepId] && "report_id" in allWorkflowSteps[stepId].task)
  ) {
    return;
  }

  return allWorkflowSteps[stepId]?.task.report_id;
};

/**
 * Extracts the run worker task configuration from the workflow template config
 * for a given step ID.
 */
export const getRunWorkerConfigFromWorkflowConfiguration = (
  stepId?: string,
  workflowConfiguration?: WorkflowType["config"]
): RunWorkerActionType | undefined => {
  if (!workflowConfiguration || !stepId) {
    return;
  }

  const allWorkflowSteps = flattenWorkflowSteps(workflowConfiguration.steps);
  const associatedStepConfig = allWorkflowSteps[stepId];

  if (
    !associatedStepConfig ||
    associatedStepConfig.type !== WorkflowStepType.SYSTEM_ACTION ||
    associatedStepConfig.task?.type !== SystemActionType.RUN_WORKER
  ) {
    return;
  }

  return associatedStepConfig.task;
};
